import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { IconButton, InputAdornment, Stack, Tab, Tabs, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import { TableCustom, PrimaryTableHeader } from 'app/components/elements/TableCustom';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { getIssuesConfig, getUsers } from 'services/option';
import {
  DEFAULT_ISSUES_UI_CONFIG,
  getAdHocDueDateColor,
  IssuesUiConfig
} from 'utils/issueDueDateColor';
import { useIssuesMaintenanceTab } from '../hooks/useIssuesMaintenanceTab';
import { AssetIssueDialog } from './AssetIssueDialog';
import { CriticalityChip } from './CriticalityChip';
import { MaintenanceIssueDialog } from './MaintenanceIssueDialog';
import {
  getCriticalityDisplay,
  DEFAULT_CRITICALITY_LEVELS,
  toCriticalitySelectOptions
} from 'utils/issueCriticality';
import {
  IssuesFiltersBar,
  IssuesFilterField,
  IssuesSearchField,
  filterFieldSx
} from './IssuesFiltersBar';
import { IssuesTabHeader } from './IssuesTabHeader';

const STATUS_OPTIONS_ACTIVE = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Resolved', value: 'RESOLVED' }
];

const STATUS_OPTIONS_ARCHIVED = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'Archived', value: 'ARCHIVED' }
];

type MaintenanceIssueTabContentProps = {
  view: 'active' | 'archived';
};

const MaintenanceIssueTabContent = ({ view }: MaintenanceIssueTabContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const assetIssueIdParam = searchParams.get('assetIssueId');
  const submitIdParam = searchParams.get('submitId');
  const submittedAssetIdParam = searchParams.get('submittedAssetId');
  const maintenanceIssueIdParam = searchParams.get('maintenanceIssueId');
  const [dialogIssueId, setDialogIssueId] = useState<number | null>(null);
  const [maintenanceDialogIssueId, setMaintenanceDialogIssueId] = useState<number | null>(null);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);

  const {
    search,
    setSearch,
    issues,
    pagingInfo,
    onSearch,
    sortOption,
    handleSort,
    handleChangePage,
    filterMaintenanceIssues,
    setfilterMaintenanceIssues,
    setToolbarFilter,
    clearFilters,
    statusFilter,
    criticalityFilter,
    assignedToFilter,
    refreshIssues,
    exportCsv,
    exporting
  } = useIssuesMaintenanceTab(view);

  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    getIssuesConfig()
      .then((res) => setIssuesConfig({ ...DEFAULT_ISSUES_UI_CONFIG, ...(res?.config || {}) }))
      .catch(() => setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG));
    getUsers({ page: 1, size: 99999 })
      .then((res) => {
        const list = (res?.users ?? res?.data ?? []) as { id: number; name?: string }[];
        setUserOptions(
          list
            .map((u) => ({ label: u.name || `User ${u.id}`, value: String(u.id) }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
        );
      })
      .catch(() => setUserOptions([]));
  }, []);

  useEffect(() => {
    const issueId = assetIssueIdParam ? Number(assetIssueIdParam) : NaN;
    if (!Number.isNaN(issueId)) {
      setDialogIssueId(issueId);
    }
  }, [assetIssueIdParam, submitIdParam, submittedAssetIdParam]);

  useEffect(() => {
    const issueId = maintenanceIssueIdParam ? Number(maintenanceIssueIdParam) : NaN;
    if (!Number.isNaN(issueId)) {
      setMaintenanceDialogIssueId(issueId);
    }
  }, [maintenanceIssueIdParam]);

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const statusOptions = useMemo(
    () => (view === 'archived' ? STATUS_OPTIONS_ARCHIVED : STATUS_OPTIONS_ACTIVE),
    [view]
  );

  const criticalityOptions = useMemo(
    () => [{ label: 'All', value: '' }, ...toCriticalitySelectOptions(criticalityLevels)],
    [criticalityLevels]
  );

  const hasActiveFilters = !!(
    filterMaintenanceIssues?.length ||
    search.trim() ||
    statusFilter ||
    criticalityFilter ||
    assignedToFilter
  );

  const headers: PrimaryTableHeader[] = useMemo(
    () => [
      { label: 'Client Name', key: 'clientName', width: 150 },
      { label: 'Location', key: 'location', width: 200 },
      { label: 'Equipment ID', key: 'equipmentId', width: 150, sort: 'equipmentId' },
      { label: 'Issue Type', key: 'type', width: 150 },
      { label: 'Description', key: 'description', width: 260, sort: 'description' },
      { label: 'Assigned By', key: 'assignedBy', width: 140 },
      { label: 'Assigned To', key: 'assignedTo', width: 140 },
      { label: 'Due Date', key: 'dueDate', width: 130, sort: 'actionByDate' },
      { label: 'Criticality', key: 'criticality', width: 120 },
      { label: 'Status', key: 'status', width: 120, sort: 'status' },
      { label: 'Date Created', key: 'createdAt', width: 150, sort: 'createdAt' },
      { label: 'Actions', key: 'actions', width: 120 }
    ],
    []
  );

  const closeDialog = useCallback(() => {
    setDialogIssueId(null);
    setSearchParams({ tab: 'assetIssues' });
  }, [setSearchParams]);

  const rows = useMemo(
    () =>
      issues.map((issue) => {
        const dueDate = issue.dueDate;
        //|| issue.actionByDate;
        // const criticality =
        //   issue.criticality || getCriticalityDisplay(issue.criticalityKey, criticalityLevels);
        return {
          clientName: {
            value: (
              <TextEllipsis
                onClick={() => {
                  if (!issue.assetClientName) return;
                  setfilterMaintenanceIssues({
                    key: 'clientId',
                    value: issue.assetClientName.toString(),
                    label: issue.assetClientName
                  });
                }}
                text={issue.assetClientName}
                width={'95%'}
                active={
                  !!filterMaintenanceIssues?.find(
                    (item) =>
                      item.key === 'clientId' && item.value === (issue.assetClientName || '')
                  )
                }
              />
            )
          },
          location: {
            value: <TextEllipsis text={issue.location} width={'95%'} />
          },
          equipmentId: {
            value: <TextEllipsis text={issue.assetName} width={'95%'} />
          },
          type: {
            // value: <TextEllipsis text={issue.type} width={'95%'} />
            value: <TextEllipsis text={''} width={'95%'} />
          },
          description: {
            value: (
              <Typography whiteSpace="pre-line" width="95%">
                {issue.description}
              </Typography>
            )
          },
          assignedBy: {
            value: <TextEllipsis text={issue?.assignedBy?.name || '-'} width={'95%'} />
          },
          assignedTo: {
            value: (
              <TextEllipsis
                text={issue?.assignedTo?.name || '-'}
                //|| issue.actionBy?.replace('other_', '')
                width={'95%'}
                active={issue.assignedTo != null && assignedToFilter === String(issue.assignedTo)}
                onClick={() => {
                  if (issue.assignedTo == null) return;
                  const id = String(issue.assignedTo);
                  setToolbarFilter(
                    'assignedToUserId',
                    assignedToFilter === id ? undefined : id,
                    issue.assignedTo.name
                  );
                }}
              />
            )
          },
          dueDate: {
            value: dueDate ? (
              <Typography
                width="95%"
                color={getAdHocDueDateColor(dueDate, issue.status, issuesConfig)}>
                {dayjs(dueDate).format('YYYY-MM-DD')}
              </Typography>
            ) : (
              <Typography fontSize={12} color="text.secondary">
                -
              </Typography>
            )
          },
          criticality: {
            // value: <CriticalityChip criticality={criticality} />
            value: <CriticalityChip />
          },
          status: {
            value: (
              <TextEllipsis
                text={issue.status}
                width={'95%'}
                active={statusFilter === issue.status}
                onClick={() => {
                  if (!issue.status) return;
                  setToolbarFilter(
                    'status',
                    statusFilter === issue.status ? undefined : issue.status
                  );
                }}
              />
            )
          },
          createdAt: {
            value: (
              <TextEllipsis
                text={dayjs(issue.createdAt).format('YYYY-MM-DD HH:mm')}
                width={'95%'}
              />
            )
          },
          actions: {
            value: (
              <Typography
                sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setMaintenanceDialogIssueId(Number(issue.id))}>
                Edit
              </Typography>
            )
          }
        };
      }),
    [
      assignedToFilter,
      criticalityLevels,
      filterMaintenanceIssues,
      issues,
      issuesConfig,
      setfilterMaintenanceIssues,
      setToolbarFilter,
      statusFilter
    ]
  );

  const title = view === 'archived' ? 'Archived Maintenance Issues' : 'Maintenance Issues';
  const description =
    view === 'archived'
      ? 'Archived maintenance issues, including those archived when a client was archived. Open an issue to change its status and move it back to the active list.'
      : 'Review and edit maintenance issues from inspections. Use filters below or click table cells to narrow the list.';

  return (
    <Stack gap={2.5} width="100%">
      <IssuesTabHeader title={title} description={description} />

      <IssuesFiltersBar
        exporting={exporting}
        onExportCsv={() => exportCsv(criticalityLevels)}
        showClearFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        helperText="Search by keyword, or filter by status, criticality, and assignee. Export downloads CSV for the current filters.">
        <IssuesSearchField onSearch={onSearch}>
          <InputText
            title="Search"
            placeholder="Keyword, equipment ID, description…"
            startAdornment={
              <InputAdornment position="end">
                <IconButton size="small" onClick={onSearch}>
                  <img alt="icon-search" src="/icons/icon_search.svg" width={20} />
                </IconButton>
              </InputAdornment>
            }
            inputProps={{
              value: search,
              onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setSearch(event.target.value);
              },
              onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (event.key === 'Enter') onSearch();
              },
              style: { minHeight: '46px', height: '46px', padding: 0 }
            }}
          />
        </IssuesSearchField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Status"
            value={statusFilter}
            placeholder="All statuses"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setToolbarFilter('status', (e.target.value as string) || undefined)
            }
            options={statusOptions}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Criticality"
            value={criticalityFilter}
            placeholder="All criticalities"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setToolbarFilter('criticalityKey', (e.target.value as string) || undefined)
            }
            options={criticalityOptions}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={12} sm={4} md={4} lg={3}>
          <InputSelect
            title="Assigned To"
            value={assignedToFilter}
            placeholder="All assignees"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setToolbarFilter('assignedToUserId', (e.target.value as string) || undefined)
            }
            options={[{ label: 'All', value: '' }, ...userOptions]}
          />
        </IssuesFilterField>
      </IssuesFiltersBar>

      <TableCustom
        isScrollX
        data={rows}
        headers={headers}
        handleChooseRow={() => {}}
        onSortHeader={handleSort}
        sort={sortOption}
        pagingInfo={pagingInfo}
        handleChangePage={handleChangePage}
      />
      <AssetIssueDialog
        isOpen={dialogIssueId != null}
        issueId={dialogIssueId ?? undefined}
        onClose={closeDialog}
        onSaved={refreshIssues}
      />
      <MaintenanceIssueDialog
        isOpen={maintenanceDialogIssueId != null}
        issueId={maintenanceDialogIssueId ?? undefined}
        item={null}
        onClose={() => {
          setMaintenanceDialogIssueId(null);
          if (searchParams.get('maintenanceIssueId')) {
            const next = new URLSearchParams(searchParams.toString());
            next.delete('maintenanceIssueId');
            setSearchParams(next);
          }
        }}
        onSaved={refreshIssues}
      />
    </Stack>
  );
};

export const MaintenanceIssueTab = () => {
  const [subTab, setSubTab] = useState<'active' | 'archived'>('active');

  return (
    <Stack paddingTop={3} gap={2}>
      <Tabs
        value={subTab}
        onChange={(_, value) => setSubTab(value)}
        sx={{ borderBottom: '1px solid #eee' }}>
        <Tab label="Maintenance Issues" value="active" />
        <Tab label="Archived Maintenance Issues" value="archived" />
      </Tabs>
      <MaintenanceIssueTabContent view={subTab} />
    </Stack>
  );
};
